import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MoreVertical, Copy, Pencil, Send, Trash2, FileText, Star, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/calculations";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

/**
 * QuoteFamilySelector — horizontal scrollable card row of every quote attached to a job.
 *
 * Active card is highlighted in HPS orange. Superseded cards are grey. Each card has an
 * overflow menu with Set-as-Active, Duplicate, Edit, Send (active only), Delete, View PDF.
 *
 * Wraps the Stage-1 backend endpoints:
 *  GET    /api/quotes/{id}/family
 *  POST   /api/quotes/{id}/set-active
 *  POST   /api/quotes/{id}/duplicate?set_active=…
 *  DELETE /api/quotes/{id}?replacement_quote_id=…&confirm_remove_last=…
 *
 * Props:
 *   currentQuoteId  — the quote whose body is being viewed below. Used to highlight the
 *                     active card with a small "Viewing" pill.
 *   onFamilyChange  — called after any successful mutation so the parent can reload its
 *                     own copy of the quote (e.g. if Active changed, the parent might want
 *                     to refetch totals).
 */
export default function QuoteFamilySelector({ currentQuoteId, onFamilyChange, mobileMode = false }) {
  const navigate = useNavigate();
  const [family, setFamily] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null); // quote_id currently being mutated

  // Route prefixes — desktop uses /quotes/:id, mobile contractor view uses /mobile/job/:id.
  // Edit pages differ similarly: /quotes/:id/edit vs /mobile/quote/:id.
  const VIEW_PREFIX = mobileMode ? "/mobile/job" : "/quotes";
  const editPath = (qid) => (mobileMode ? `/mobile/quote/${qid}` : `/quotes/${qid}/edit`);

  // Modal state — only one open at a time.
  const [setActiveTarget, setSetActiveTarget] = useState(null); // {id, label}
  const [duplicateTarget, setDuplicateTarget] = useState(null); // {id, label}
  const [deleteTarget, setDeleteTarget] = useState(null);       // {id, label, isActive, hasSiblings}
  const [replacementForDelete, setReplacementForDelete] = useState("");

  // Controlled per-card menu state. We close the menu BEFORE opening any confirmation
  // dialog so the user gets clean visual feedback ("click registered, menu went away").
  const [openMenuId, setOpenMenuId] = useState(null);

  // Synchronous in-flight guard. setBusyId (React state) updates async — between Click 1
  // firing the handler and React re-rendering with disabled=true, a fast double-click can
  // fire the handler twice. The ref check below traps that within the same JS tick and
  // prevents two concurrent API calls (which would race + double-fire toasts).
  const inFlightRef = useRef(false);

  // Helper: close the menu via controlled state AND open the dialog in the same tick.
  // React batches both state updates so the menu unmounts and the dialog mounts together.
  // No setTimeout/rAF — those introduced timing differences across browsers that some
  // users perceived as "needing to click twice".
  const openAfterMenu = useCallback((dialogSetter) => {
    setOpenMenuId(null);
    dialogSetter();
  }, []);

  const loadFamily = useCallback(async () => {
    if (!currentQuoteId) return;
    try {
      const res = await api.get(`/quotes/${currentQuoteId}/family`);
      setFamily(res.data?.family || []);
    } catch (err) {
      console.error("Failed to load quote family", err);
    } finally {
      setLoading(false);
    }
  }, [currentQuoteId]);

  useEffect(() => { loadFamily(); }, [loadFamily]);

  const activeQuote = family.find(q => q.is_active);
  const userName = (typeof localStorage !== "undefined" && localStorage.getItem("desktop_user_name")) || "Office user";

  // ── Actions ─────────────────────────────────────────────────────────────────
  const handleSetActive = async () => {
    if (!setActiveTarget) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const target = setActiveTarget; // snapshot for the post-success path; React state may flip to null mid-flight
    setBusyId(target.id);
    let apiSucceeded = false;
    try {
      await api.post(`/quotes/${target.id}/set-active`, { user: userName });
      apiSucceeded = true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to change Active Quote");
    }
    // Post-API path is intentionally OUTSIDE the try/catch above — once the
    // server returned 200, the operation succeeded. Any phantom throw from
    // loadFamily/onFamilyChange must NOT trigger the "Failed" toast.
    if (apiSucceeded) {
      toast.success(`${target.label} is now the Active Quote`);
      setSetActiveTarget(null);
      try { await loadFamily(); } catch (e) { console.error("loadFamily post-set-active failed", e); }
      try { onFamilyChange?.(); } catch (e) { console.error("onFamilyChange post-set-active failed", e); }
    }
    setBusyId(null);
    inFlightRef.current = false;
  };

  const handleDuplicate = async (setActive) => {
    if (!duplicateTarget) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const target = duplicateTarget;
    setBusyId(target.id);
    try {
      const params = new URLSearchParams({
        set_active: setActive ? "true" : "false",
        user: userName,
      }).toString();
      const res = await api.post(`/quotes/${target.id}/duplicate?${params}`);
      toast.success(`Duplicated as ${res.data.duplicate_label}${setActive ? " and set as Active" : ""}`);
      setDuplicateTarget(null);
      // Per spec: open the new duplicated quote in edit mode immediately.
      navigate(editPath(res.data.id));
    } catch (err) {
      console.error(err);
      toast.error("Failed to duplicate");
    } finally {
      setBusyId(null);
      inFlightRef.current = false;
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const target = deleteTarget;
    setBusyId(target.id);
    let apiSucceeded = false;
    let resData = null;
    try {
      const qs = new URLSearchParams({ user: userName });
      if (target.isActive && target.hasSiblings) {
        if (!replacementForDelete) {
          toast.error("Choose a new Active Quote first");
          setBusyId(null);
          inFlightRef.current = false;
          return;
        }
        qs.set("replacement_quote_id", replacementForDelete);
      } else if (!target.hasSiblings) {
        qs.set("confirm_remove_last", "true");
      }
      const res = await api.delete(`/quotes/${target.id}?${qs.toString()}`);
      resData = res.data;
      apiSucceeded = true;
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
    if (apiSucceeded) {
      toast.success(`Deleted ${target.label}${resData.new_active_label ? ` — ${resData.new_active_label} promoted to Active` : ""}`);
      // If we just deleted the currently-viewed quote, navigate to the new Active.
      if (target.id === currentQuoteId) {
        if (resData.new_active_quote_id) {
          navigate(`${VIEW_PREFIX}/${resData.new_active_quote_id}`, { replace: true });
        } else {
          navigate(mobileMode ? "/mobile/today" : "/", { replace: true });
        }
        setBusyId(null);
        inFlightRef.current = false;
        return;
      }
      setDeleteTarget(null);
      setReplacementForDelete("");
      try { await loadFamily(); } catch (e) { console.error("loadFamily post-delete failed", e); }
      try { onFamilyChange?.(); } catch (e) { console.error("onFamilyChange post-delete failed", e); }
    }
    setBusyId(null);
    inFlightRef.current = false;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="px-2 py-3 flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading quotes…
      </div>
    );
  }
  if (!family.length) return null;

  return (
    <div className="px-2" data-testid="quote-family-selector">
      <div className="flex items-center justify-between mb-2 px-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>
          Quotes on this job ({family.length})
        </h3>
        {family.length > 1 && activeQuote && (
          <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
            Active: <strong style={{ color: "var(--primary)" }}>{activeQuote.duplicate_label || "Option 1"}</strong>
          </span>
        )}
      </div>

      <div className="overflow-x-auto pb-3" style={{ scrollbarWidth: "thin" }}>
        <div className="flex gap-3 px-2" style={{ minWidth: "min-content" }}>
          {family.map((q) => {
            const isViewing = q.id === currentQuoteId;
            const isActive = q.is_active;
            const label = q.duplicate_label || "Option 1";
            const hasSiblings = family.length > 1;

            return (
              <div
                key={q.id}
                data-testid={`quote-card-${q.id}`}
                onClick={(e) => {
                  // Don't navigate when clicking the overflow menu trigger.
                  if (e.target.closest("[data-overflow-trigger]")) return;
                  if (isViewing) return;
                  // Use replace so sibling-card navigation within the same Job Pack
                  // doesn't pollute browser history. Otherwise the user has to press
                  // Back multiple times to escape the family (once per sibling viewed).
                  navigate(`/quotes/${q.id}`, { replace: true });
                }}
                style={{
                  width: 260,
                  minWidth: 260,
                  cursor: isViewing ? "default" : "pointer",
                  border: isActive ? "2px solid var(--primary)" : "1px solid var(--border)",
                  background: isActive ? "var(--primary-light, #FFF5EE)" : (isViewing ? "var(--bg)" : "#FFFFFF"),
                  borderRadius: 12,
                  padding: 14,
                  boxShadow: isViewing ? "0 0 0 2px var(--primary-light, rgba(224,92,42,0.2))" : "0 1px 2px rgba(0,0,0,0.04)",
                  transition: "all 0.15s ease",
                  opacity: isActive ? 1 : 0.92,
                  position: "relative",
                }}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>{label}</span>
                    {isActive ? (
                      <span data-testid={`active-pill-${q.id}`} className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
                        style={{ background: "var(--primary)", color: "#fff" }}>Active</span>
                    ) : (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
                        style={{ background: "#E5E7EB", color: "#6B7280" }}>Inactive</span>
                    )}
                    {isViewing && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
                        style={{ background: "#DBEAFE", color: "#1E40AF" }}>Viewing</span>
                    )}
                  </div>
                  <DropdownMenu open={openMenuId === q.id} onOpenChange={(o) => setOpenMenuId(o ? q.id : null)}>
                    <DropdownMenuTrigger
                      data-overflow-trigger
                      data-testid={`quote-card-menu-${q.id}`}
                      className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-black/5 transition"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      {/* Every action that opens a confirmation Dialog goes through openAfterMenu().
                          That helper closes the menu, then waits two animation frames for Radix to
                          finish its menu-close cleanup (focus return + body pointer-events restore)
                          before mounting the Dialog. Without it, the menu's close cleanup races
                          the Dialog mount and the user had to click "Set as Active" twice. */}
                      <DropdownMenuItem
                        disabled={isActive || busyId === q.id}
                        onSelect={(e) => { e.preventDefault(); openAfterMenu(() => setSetActiveTarget({ id: q.id, label })); }}
                        data-testid={`set-active-${q.id}`}
                      >
                        <Star className="w-4 h-4 mr-2" />
                        Set as Active Quote
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={(e) => { e.preventDefault(); openAfterMenu(() => setDuplicateTarget({ id: q.id, label })); }}
                        data-testid={`duplicate-${q.id}`}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Duplicate Quote
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => { setOpenMenuId(null); navigate(editPath(q.id)); }}
                        data-testid={`edit-${q.id}`}
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit Quote
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={!isActive}
                        onSelect={() => { setOpenMenuId(null); navigate(`${VIEW_PREFIX}/${q.id}`); }}
                        data-testid={`send-${q.id}`}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {isActive ? "Send Quote" : "Send (Active only)"}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => { setOpenMenuId(null); window.open(`/api/quotes/${q.id}/pdf`, "_blank"); }}
                        data-testid={`view-pdf-${q.id}`}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        View Quote PDF
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={(e) => { e.preventDefault(); openAfterMenu(() => setDeleteTarget({ id: q.id, label, isActive, hasSiblings })); }}
                        className="text-red-600 focus:text-red-600 focus:bg-red-50"
                        data-testid={`delete-${q.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Quote
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="text-xs mb-1.5" style={{ color: "var(--text-muted)" }}>
                  {q.quote_number || "—"} · {(q.created_at || "").slice(0, 10)}
                </div>
                <div className="text-lg font-bold" style={{ color: isActive ? "var(--primary)" : "var(--text-primary)" }}>
                  {formatCurrency(q.total_inc_vat || 0)}
                </div>
                <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                  inc VAT · {q.item_count || 0} items
                </div>
                {q.status && (
                  <div className="mt-2">
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                      style={{ background: "#F3F4F6", color: "#374151" }}>{q.status}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Set Active confirmation */}
      <Dialog open={!!setActiveTarget} onOpenChange={(o) => !o && setSetActiveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Make {setActiveTarget?.label} the Active Quote?</DialogTitle>
            <DialogDescription>
              The Active Quote drives the Dashboard, Job Pack summary, sending, and invoicing.
              {activeQuote && setActiveTarget && setActiveTarget.id !== activeQuote.id && (
                <> {activeQuote.duplicate_label || "Option 1"} will become Inactive.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetActiveTarget(null)}>Cancel</Button>
            <Button
              onClick={handleSetActive}
              disabled={busyId === setActiveTarget?.id}
              style={{ background: "var(--primary)", color: "#fff", borderColor: "var(--primary)" }}
              className="hover:opacity-90"
              data-testid="confirm-set-active"
            >
              {busyId === setActiveTarget?.id ? "Updating…" : "Make Active"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate prompt */}
      <Dialog open={!!duplicateTarget} onOpenChange={(o) => !o && setDuplicateTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate {duplicateTarget?.label}</DialogTitle>
            <DialogDescription>
              Set the duplicated quote as the new Active Quote when saved?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDuplicate(false)} disabled={busyId === duplicateTarget?.id} data-testid="duplicate-keep-superseded">
              Keep as Inactive
            </Button>
            <Button
              onClick={() => handleDuplicate(true)}
              disabled={busyId === duplicateTarget?.id}
              style={{ background: "var(--primary)", color: "#fff", borderColor: "var(--primary)" }}
              className="hover:opacity-90"
              data-testid="duplicate-set-active"
            >
              {busyId === duplicateTarget?.id ? "Duplicating…" : "Yes, set as Active"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete — 3 branches */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setReplacementForDelete(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?.label}?</DialogTitle>
            <DialogDescription>
              {!deleteTarget?.isActive && deleteTarget?.hasSiblings && (
                <>This action cannot be undone. The quote PDF and all its line items will be removed.</>
              )}
              {deleteTarget?.isActive && deleteTarget?.hasSiblings && (
                <>
                  {deleteTarget.label} is the Active Quote. Before deleting it, choose a new
                  Active Quote from the list below. Then the original quote is deleted and the
                  chosen quote becomes Active.
                </>
              )}
              {deleteTarget && !deleteTarget.hasSiblings && (
                <>
                  {deleteTarget.label} is the only quote on this job. Deleting it will leave the
                  job without any quote attached. The job will need a new quote created before it
                  can be invoiced. Continue?
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {deleteTarget?.isActive && deleteTarget?.hasSiblings && (
            <div className="space-y-2">
              <label className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
                New Active Quote:
              </label>
              <select
                value={replacementForDelete}
                onChange={(e) => setReplacementForDelete(e.target.value)}
                className="w-full px-3 py-2 rounded-md border text-sm"
                style={{ borderColor: "var(--border)" }}
                data-testid="delete-replacement-select"
              >
                <option value="">— pick one —</option>
                {family.filter(q => q.id !== deleteTarget.id).map(q => (
                  <option key={q.id} value={q.id}>
                    {q.duplicate_label || "Option 1"} · {formatCurrency(q.total_inc_vat || 0)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setReplacementForDelete(""); }}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={busyId === deleteTarget?.id || (deleteTarget?.isActive && deleteTarget?.hasSiblings && !replacementForDelete)}
              data-testid="confirm-delete-quote"
            >
              {busyId === deleteTarget?.id ? "Deleting…" : (deleteTarget?.isActive && deleteTarget?.hasSiblings ? "Delete & Promote" : "Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
